/**
 * ПЕРЕСЫЛКА (FORWARD) СООБЩЕНИЯ В ДРУГУЮ КОМНАТУ.
 *
 * История: 05.10 12:37 — «в вебе её НЕТ вообще», сделана по образцу iOS и
 * пересылала только текст. 15.10 Антон: «Пересылать можно любые типы
 * сообщений» → «Тоже самое сделай для вэб». Та же правка параллельно
 * делается на iOS.
 *
 * ═══ ЧТО ПЕРЕСЫЛАЕТСЯ ═══
 *
 * ЛЮБОЕ сообщение с содержимым: текст / notice / emote, картинка, файл,
 * видео, аудио, голосовое, стикер (m.sticker), локация, опрос и любой
 * другой msgtype. Белого списка msgtype НЕТ.
 *
 * Прежнее обоснование «только текст» было неверным: якобы ключи файла
 * привязаны к комнате и чужой `file` у получателя не расшифруется. На деле
 * ключ EncryptedFile (`file.key`, `file.iv`, `file.hashes`) лежит В САМОМ
 * СОБЫТИИ, а не в комнате: кто получил событие, тот может расшифровать файл
 * в любой комнате. К тому же шифрование в наших комнатах выключено, и у
 * вложений просто `url: mxc://…`. Скачивать и перезаливать файл не нужно —
 * mxc остаётся тем же, медиа на сервере одно.
 *
 * НЕ пересылаются (и пункта меню у них нет):
 *  - удалённое (надгробие) — содержимого нет;
 *  - нерасшифрованное — содержимого у нас нет;
 *  - локальное эхо (pending / failed) — серверного события ещё нет;
 *  - служебные строки (звонок, вход/выход, смена названия и т. п.) — это не
 *    сообщения, а состояние комнаты.
 *
 * ═══ КАК ОТПРАВЛЯЕТСЯ ═══
 *
 * Берётся ИСХОДНЫЙ content события (MatrixEvent, а не собранный для ленты
 * TimelineItem — в нём уже нет info, url, waveform и прочего), из него
 * убираются `m.relates_to` и `m.new_content`, и уходит НОВОЕ событие ТОГО
 * ЖЕ ТИПА в целевую комнату: `m.room.message` — как m.room.message,
 * `m.sticker` — как m.sticker. Все прочие поля сохраняются как есть:
 * msgtype, body, format/formatted_body, info, url/file, у голосовых
 * `org.matrix.msc1767.audio` и `org.matrix.msc3245.voice`.
 *
 * ОТРЕДАКТИРОВАННОЕ сообщение пересылается в актуальной версии:
 * `m.new_content` правки, если она есть, иначе исходный content.
 *
 * ОТВЕТ: вместе с `m.relates_to` срезается и fallback-цитата
 * («> <@кто> …» в body и <mx-reply> в formatted_body) — иначе получатель
 * увидел бы цитату сообщения из чужого чата склеенной с текстом.
 *
 * Цитаты «переслано от …» НЕТ: в Matrix нет стандартного отношения
 * «forward»; Element вставляет обычное сообщение без `m.relates_to`, своё
 * поле не понял бы ни один другой клиент, а текстовая приписка — подделка
 * содержимого. Реакции и тред не переносятся.
 *
 * Отправка — через `client.sendEvent` (та же очередь, что у обычной
 * отправки: локальное эхо, повтор при сбое сети). Таймлайн целевой комнаты
 * не создаётся. Ошибка возвращается исключением, показывает её диалог.
 */
import type { IContent, MatrixClient, MatrixEvent } from "matrix-js-sdk";
import { EventType } from "matrix-js-sdk";
import type { TimelineItem } from "./timeline";
import { withRateLimit } from "./roomActions";
import { replyTargetId, stripFormattedReplyFallback, stripReplyFallback } from "./relations";

/** Типы событий, которые лента показывает как сообщение и которые можно
    переслать. m.room.message покрывает любой msgtype (текст, медиа,
    голосовые, локации, произвольные). Опросы MSC3381 приходят своим типом. */
const FORWARDABLE_EVENT_TYPES: ReadonlySet<string> = new Set([
  EventType.RoomMessage,
  EventType.Sticker,
  "m.poll.start",
  "org.matrix.msc3381.poll.start",
]);

/**
 * Содержимое для пересылки: актуальная версия (с учётом правки) исходного
 * события без `m.relates_to` / `m.new_content` и без fallback-цитаты
 * ответа. null — пересылать нечего (удалено, не расшифровано, служебное,
 * пустое). Чистая функция над MatrixEvent — её и проверяют тесты.
 */
export function buildForwardContent(ev: MatrixEvent): { type: string; content: IContent } | null {
  if (ev.isRedacted() || ev.isState()) return null;
  if (ev.isDecryptionFailure() || ev.getType() === EventType.RoomMessageEncrypted) return null;
  const type = ev.getType();
  if (!FORWARDABLE_EVENT_TYPES.has(type)) return null;
  // Сама правка (m.replace) — не сообщение, а изменение другого.
  if (ev.getRelation()?.rel_type === "m.replace") return null;

  const original = ev.getOriginalContent();
  const replacing = ev.replacingEvent();
  const edited = replacing?.getContent()?.["m.new_content"] as IContent | undefined;
  let content: IContent = { ...(edited && typeof edited === "object" ? edited : original) };
  // Правка подписи у медиа по спеке несёт полный content, но если чужой
  // клиент прислал m.new_content без самого файла — файл берём из оригинала,
  // иначе пересланное вложение потеряло бы содержимое.
  if (edited && !content.url && !content.file && (original.url || original.file)) {
    content = {
      ...content,
      ...(original.url ? { url: original.url } : {}),
      ...(original.file ? { file: original.file } : {}),
      ...(original.info && !content.info ? { info: original.info } : {}),
    };
  }
  delete content["m.relates_to"];
  delete content["m.new_content"];

  // Fallback-цитата ответа живёт в исходном content (у правки её нет).
  if (!edited && replyTargetId(ev) !== null) {
    if (typeof content.body === "string") content.body = stripReplyFallback(content.body, true);
    if (typeof content.formatted_body === "string") {
      content.formatted_body = stripFormattedReplyFallback(content.formatted_body, true);
    }
  }

  if (Object.keys(content).length === 0) return null;
  // m.room.message без msgtype — не сообщение (так выглядит пустой/битый content).
  if (type === EventType.RoomMessage && typeof content.msgtype !== "string") return null;
  return { type, content };
}

/**
 * Можно ли переслать именно это сообщение — предикат и для пункта меню, и
 * для самой отправки. ОДИН источник истины: два правила в двух местах
 * разошлись бы, и пункт меню показывался бы там, где сервис откажет.
 *
 * `ev` — исходное MatrixEvent этой строки ленты. Без него переслать нечего:
 * TimelineItem — проекция для рендера, оригинального content в нём нет.
 */
export function canForwardMessage(item: TimelineItem, ev: MatrixEvent | null | undefined): boolean {
  if (item.redacted || item.pending || item.failed) return false;
  if (item.system || item.call) return false;
  if (!ev) return false;
  return buildForwardContent(ev) !== null;
}

/**
 * Переслать сообщение в другую комнату. Бросает Error при отказе —
 * текст для показа человеку собирает вызывающий UI через humanError.
 */
export async function forwardMessage(
  client: MatrixClient,
  item: TimelineItem,
  ev: MatrixEvent | null | undefined,
  toRoomId: string,
): Promise<void> {
  const built = ev && canForwardMessage(item, ev) ? buildForwardContent(ev) : null;
  if (!built) throw new Error("forward: unsupported message");
  // sendEvent типизирован по карте TimelineEvents; тип события здесь —
  // проверенная выше строка из FORWARDABLE_EVENT_TYPES, content — исходный
  // content того же типа. Каст сообщает компилятору уже проверенное.
  const send = client.sendEvent.bind(client) as unknown as (
    roomId: string,
    eventType: string,
    content: IContent,
  ) => Promise<unknown>;
  await withRateLimit(() => send(toRoomId, built.type, built.content));
}
